/**
 * How the dashboard reads its data: show what is already cached at once, then
 * refresh it in the background — so a return to the dashboard paints
 * immediately instead of behind spinners, and is never older than a visit
 * used to be.
 *
 * The one exception is a request the app started during boot for this very
 * screen (`app/preloadDashboardRoute`). The page uses that result as it is (or
 * joins it while it is still in flight) instead of asking again a moment
 * later.
 */

interface CacheEntry<Data> {
  data?: Data;
  requestId?: string;
}

interface Endpoint<Args, Data> {
  select: (args: Args) => (state: never) => CacheEntry<Data>;
}

interface TriggerResult<Data> {
  data?: Data;
  error?: unknown;
}

interface ReadThroughOptions<Data> {
  /** Called with cached data first (when there is some), then with fresh. */
  onData: (data: Data) => void;
  /** Nothing to show yet: the caller shows its loader. */
  onLoading?: () => void;
  /** The request failed and nothing cached was shown. */
  onError?: (error: unknown) => void;
}

const bootRequestIds = new Set<string>();

/** Marks a request started at boot as the one the page should use as-is. */
export const markBootRequest = (requestId: string): void => {
  bootRequestIds.add(requestId);
};

/** True the first time a boot request is asked about; it is then used up. */
const consumeBootRequest = (requestId: string | undefined): boolean =>
  Boolean(requestId) && bootRequestIds.delete(requestId as string);

export const readThrough = async <Args, Data>(
  endpoint: Endpoint<Args, Data>,
  args: Args,
  getState: () => unknown,
  trigger: (
    args: Args,
    preferCacheValue?: boolean
  ) => PromiseLike<TriggerResult<Data>>,
  { onData, onLoading, onError }: ReadThroughOptions<Data>
): Promise<void> => {
  const entry = endpoint.select(args)(getState() as never);
  const hasCached = entry?.data !== undefined;
  const fromBoot = consumeBootRequest(entry?.requestId);

  if (hasCached) {
    onData(entry.data as Data);
    // Fetched for this screen moments ago: asking again would only repeat it.
    // Still go through the trigger, preferring the cached value: that sends
    // nothing but subscribes this screen to the entry. The boot request lets
    // go of it once done, so without this it expired while the dashboard was
    // still open and the next return showed spinners again.
    if (fromBoot) {
      void trigger(args, true);
      return;
    }
  } else {
    onLoading?.();
  }

  // A lazy trigger refetches, but joins a request already in flight for the
  // same arguments (e.g. the boot one) instead of sending another.
  const result = await trigger(args);
  if (result?.data !== undefined) {
    onData(result.data);
  } else if (!hasCached) {
    onError?.(result?.error);
  }
};
